<script setup lang="ts">
import {computed} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import {storeToRefs} from 'pinia'
import {useAppStore} from '@/stores/app'
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuBadge,
  useSidebar,
} from '@/components/ui/sidebar'
import {
  Search,
  Inbox,
  ArrowRight,
  Hourglass,
  Calendar,
  Sun,
  Star,
  CalendarCheck,
  FolderClosed,
  Archive,
  Trash2,
} from 'lucide-vue-next'
import type {TaskWithInstance} from '@backend/types'
import {isAgendaTask} from '@/utils/agenda'

const route = useRoute()
const router = useRouter()
const appStore = useAppStore()
const {allTasks, projects} = storeToRefs(appStore)
const {isMobile, setOpenMobile} = useSidebar()

const isTaskIncomplete = (task: TaskWithInstance) => task.instances.some((instance) => !instance.done)

const counters = computed(() => ({
  agenda: allTasks.value.filter((t) => isAgendaTask(t)).length,
  inbox: allTasks.value.filter((t) => t.folder === 'inbox' && isTaskIncomplete(t)).length,
  next: allTasks.value.filter((t) => t.folder === 'next' && isTaskIncomplete(t)).length,
  waiting: allTasks.value.filter((t) => t.folder === 'waiting' && isTaskIncomplete(t)).length,
  scheduled: allTasks.value.filter((t) => t.folder === 'scheduled' && isTaskIncomplete(t)).length,
  someday: allTasks.value.filter((t) => t.folder === 'someday' && isTaskIncomplete(t)).length,
  focus: allTasks.value.filter((t) => t.focused && isTaskIncomplete(t)).length,
}))

const getProjectTaskCount = (projectId: number) =>
  allTasks.value.filter((t) => t.project_id === projectId && isTaskIncomplete(t)).length

const isActive = (target: {name?: string; folder?: string; projectId?: number}) => {
  if (target.name === 'search') return route.name === 'search'
  if (target.name === 'focus') return route.name === 'focus'
  if (target.name === 'agenda') return route.name === 'agenda'
  if (target.projectId !== undefined)
    return route.name === 'todo-project' && String(route.params.projectId) === String(target.projectId)
  if (target.folder) return route.name === 'todo' && route.params.folder === target.folder
  return false
}

function navigate(to: {name: string; params?: Record<string, string | number>}) {
  router.push(to)
  if (isMobile.value) setOpenMobile(false)
}

function handleFolderClick(folder: string) {
  navigate({name: 'todo', params: {folder}})
}

function handleProjectClick(projectId: number) {
  navigate({name: 'todo-project', params: {projectId}})
}

type CounterKey = keyof typeof counters.value

interface NavItem {
  key: string
  icon: typeof Search
  labelKey: string
  counter?: CounterKey
  active: {name?: string; folder?: string}
  click: () => void
}

const navSections: {titleKey: string; items: NavItem[]}[] = [
  {
    titleKey: 'sidebar.search',
    items: [
      {
        key: 'search',
        icon: Search,
        labelKey: 'actions.search',
        active: {name: 'search'},
        click: () => navigate({name: 'search'}),
      },
    ],
  },
  {
    titleKey: 'sidebar.capture',
    items: [
      {
        key: 'inbox',
        icon: Inbox,
        labelKey: 'scopes.inbox',
        counter: 'inbox',
        active: {folder: 'inbox'},
        click: () => handleFolderClick('inbox'),
      },
    ],
  },
  {
    titleKey: 'sidebar.actions',
    items: [
      {
        key: 'agenda',
        icon: CalendarCheck,
        labelKey: 'scopes.agenda',
        counter: 'agenda',
        active: {name: 'agenda'},
        click: () => navigate({name: 'agenda'}),
      },
      {
        key: 'next',
        icon: ArrowRight,
        labelKey: 'scopes.next',
        counter: 'next',
        active: {folder: 'next'},
        click: () => handleFolderClick('next'),
      },
      {
        key: 'waiting',
        icon: Hourglass,
        labelKey: 'scopes.waiting',
        counter: 'waiting',
        active: {folder: 'waiting'},
        click: () => handleFolderClick('waiting'),
      },
      {
        key: 'scheduled',
        icon: Calendar,
        labelKey: 'scopes.scheduled',
        counter: 'scheduled',
        active: {folder: 'scheduled'},
        click: () => handleFolderClick('scheduled'),
      },
      {
        key: 'someday',
        icon: Sun,
        labelKey: 'scopes.someday',
        counter: 'someday',
        active: {folder: 'someday'},
        click: () => handleFolderClick('someday'),
      },
      {
        key: 'focus',
        icon: Star,
        labelKey: 'scopes.focus',
        counter: 'focus',
        active: {name: 'focus'},
        click: () => navigate({name: 'focus'}),
      },
    ],
  },
  {
    titleKey: 'sidebar.cleanup',
    items: [
      {
        key: 'archived',
        icon: Archive,
        labelKey: 'scopes.archived',
        active: {folder: 'archived'},
        click: () => handleFolderClick('archived'),
      },
      {
        key: 'trash',
        icon: Trash2,
        labelKey: 'scopes.trash',
        active: {folder: 'trash'},
        click: () => handleFolderClick('trash'),
      },
    ],
  },
]
</script>

<template>
  <SidebarGroup v-for="section in navSections" :key="section.titleKey">
    <SidebarGroupLabel>{{ $t(section.titleKey) }}</SidebarGroupLabel>
    <SidebarGroupContent>
      <SidebarMenu>
        <SidebarMenuItem v-for="item in section.items" :key="item.key">
          <SidebarMenuButton :is-active="isActive(item.active)" @click="item.click">
            <component :is="item.icon" />
            <span>{{ $t(item.labelKey) }}</span>
          </SidebarMenuButton>
          <SidebarMenuBadge v-if="item.counter && counters[item.counter] > 0">
            {{ counters[item.counter] }}
          </SidebarMenuBadge>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarGroupContent>
  </SidebarGroup>

  <SidebarGroup>
    <SidebarGroupLabel>{{ $t('sidebar.projects') }}</SidebarGroupLabel>
    <SidebarGroupContent>
      <SidebarMenu>
        <SidebarMenuItem v-for="project in projects" :key="project.project_id">
          <SidebarMenuButton
            :is-active="isActive({projectId: project.project_id})"
            @click="handleProjectClick(project.project_id)">
            <FolderClosed />
            <span>{{ project.title }}</span>
          </SidebarMenuButton>
          <SidebarMenuBadge v-if="getProjectTaskCount(project.project_id) > 0">
            {{ getProjectTaskCount(project.project_id) }}
          </SidebarMenuBadge>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarGroupContent>
  </SidebarGroup>
</template>
